import React, { useEffect, useMemo } from "react";

import { useMainPageTitle } from "../../../contexts/PageContext";
import LoadingSpinner from "../../../components/common/LoadingSpinner";
import ErrorMessage from "../../../components/common/ErrorMessage";
import CurrentSubscriptionCard from "../../../components/organization/subscription/CurrentSubscriptionCard";
import UpgradePackageCard from "../../../components/organization/subscription/UpgradePackageCard";
import PendingPaymentCard from "../../../components/organization/subscription/PendingPaymentCard";
import useSubscriptionUpgrade from "../../../hooks/useSubscriptionUpgrade";
import usePendingPayment from "../../../hooks/usePendingPayment";

const SubscriptionUpgrade = () => {

  const { updatePageTitle } = useMainPageTitle();
  const { currentSubscription, upgradePackages, loading, error, refetch } = useSubscriptionUpgrade();
  const { pendingPayment, loading: pendingLoading, error: pendingError, refetch: refetchPending } = usePendingPayment();

  useEffect(() => {
    updatePageTitle("Upgrade Subscription", "Upgrade your plan and get more features");
  }, [updatePageTitle]);

  // Get current package details from the subscription data
  const currentPackageDetails = useMemo(() => {
    if (!currentSubscription) return null;
    
    // Use the subscription data directly from the API
    return {
      id: currentSubscription.package?.toString(),
      name: currentSubscription.packageName || "Unknown",
      price: currentSubscription.packagePrice || 0,
      features: currentSubscription.packageFeatures || {}
    };
  }, [currentSubscription]);

  const handleSelectPackage = (selectedPackage) => {
    // TODO: Implement package upgrade logic
    console.log('Selected upgrade package:', selectedPackage);
    // This could navigate to a payment page or open a modal
  };

  if (loading || pendingLoading) {
    return <LoadingSpinner message="Loading subscription details..." />;
  }

  if (error || pendingError) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <ErrorMessage message={error || pendingError} />
        <div className="text-center mt-4">
          <button 
            onClick={() => {
              refetch();
              refetchPending();
            }}
            className="px-6 py-2 text-white rounded-lg transition-colors shadow-lg"
            style={{ backgroundColor: '#DE561F' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#C54E1C'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#DE561F'}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // If user has pending payment, show pending payment details
  if (pendingPayment) {
    const handleRetryPayment = async (packageId) => {
      try {
        console.log(`🔄 Creating new upgrade payment link for package: ${packageId}`);

        // Call backend payment endpoint to create new payment link
        const response = await fetch(`https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1/organization/subscription/pay`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            packageId: packageId
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Failed to create new payment link');
        }

        if (data.success && data.data.type === 'geidea' && data.data.paymentLink) {
          // Open new payment link
          window.open(data.data.paymentLink, '_blank');
          
          // Refresh pending payment data
          refetchPending();
          
          alert(`💳 New upgrade payment link created and opened in new tab.\n\nPlease complete the payment and return to this page.`);
        } else {
          throw new Error('Failed to create payment link');
        }

      } catch (error) {
        console.error('💥 Retry upgrade payment error:', error);
        alert(`❌ Error creating new payment link\n\n${error.message}\n\nPlease try again or contact support.`);
      }
    };

    return (
      <div className="">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Header Section */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Complete Your Upgrade Payment
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              You have a pending upgrade payment. Complete it to upgrade your subscription.
            </p>
          </div>

          {/* Pending Payment Card */}
          <PendingPaymentCard 
            pendingPayment={pendingPayment}
            onRetryPayment={handleRetryPayment}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Upgrade Your Subscription
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get more features and capabilities with an upgraded plan. 
            You'll only pay the difference with credit from your current subscription.
          </p>
        </div>

        {/* Current Subscription */}
        {currentSubscription && currentPackageDetails && (
          <CurrentSubscriptionCard 
            subscription={currentSubscription}
            packageDetails={currentPackageDetails}
          />
        )}

        {/* Upgrade Options */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            Available Upgrades
          </h2>
          
          {upgradePackages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upgradePackages.map((pkg) => (
                <UpgradePackageCard 
                  key={pkg.id} 
                  pkg={pkg}
                  originalPrice={pkg.originalPrice || pkg.price}
                  onSelectPackage={handleSelectPackage}
                  currentPackageFeatures={currentPackageDetails?.features}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-2">You're on the highest plan!</h3>
              <p className="text-gray-600">
                You're already enjoying our most comprehensive package with all available features.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionUpgrade; 